import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { IGNORE_ERRORS_KEY } from '@core';
import { FileStorageApi } from '@core/api/file-storage/api';

import { PdfAnnotationOverlayService } from '../services/pdf-annotation-overlay.service';
import { SimplePdfHighlightService } from '../services/simple-pdf-highlight.service';
import { AreaSelectionState, DomRectLike } from '../types';
import {
  AnnotationRect,
  PdfAnnotation,
  PdfAnnotationDraft,
  PdfSelectionMode,
  VeevaAnnotation,
} from '@core/api/sources/types';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  imports: [MatProgressSpinner, MatIcon, MatButton, NgxExtendedPdfViewerModule],
})
export class PdfViewerComponent implements OnDestroy {
  @Output() viewerReady = new EventEmitter<void>();
  @Output() viewerFailed = new EventEmitter<void>();
  @Output() annotationClicked = new EventEmitter<PdfAnnotation>();
  @Output() annotationDraftCreated = new EventEmitter<PdfAnnotationDraft>();
  @Input() showMinimalToolbar = false;

  public pdfSrc: string | Uint8Array | undefined;
  public isLoading = false;
  public hasError = false;
  public errorMessage = '';
  public currentPage = 1;

  private _fileUrl: string | undefined;
  private _selectionMode: PdfSelectionMode = 'none';
  private _needsViewerReadyEmit = false;
  private _areaSelection: AreaSelectionState | null = null;
  private readonly _cleanupListeners: Array<() => void> = [];

  constructor(
    private readonly _http: HttpClient,
    private readonly _simplePdfHighlightService: SimplePdfHighlightService,
    private readonly _overlayService: PdfAnnotationOverlayService,
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _zone: NgZone
  ) {
    this._registerInteractionListeners();
  }

  get hasFileUrl(): boolean {
    return !!this._fileUrl;
  }

  get currentFileUrl(): string | undefined {
    return this._fileUrl;
  }

  public getFileExtension(): string {
    if (!this._fileUrl) return '';
    const path = this._getFileUrlPath(this._fileUrl).toLowerCase();
    if (path.endsWith('/pdf')) {
      return 'pdf';
    }

    return path.split('/').pop()?.split('.').pop() || '';
  }

  private isPdfFile(): boolean {
    return this.getFileExtension() === 'pdf';
  }

  private _getFileUrlPath(fileUrl: string): string {
    try {
      return new URL(fileUrl).pathname;
    } catch {
      return fileUrl.split('?')[0] ?? fileUrl;
    }
  }

  public getDirectUrl(): string {
    if (!this._fileUrl) return '';
    return FileStorageApi.download(this._fileUrl).url;
  }

  public getViewerDescription(): string {
    const extension = this.getFileExtension().toUpperCase();
    return `${extension} - Extended PDF Viewer`;
  }

  public get selectionMode(): PdfSelectionMode {
    return this._selectionMode;
  }

  @Input() set selectionMode(mode: PdfSelectionMode) {
    if (mode === this._selectionMode) {
      return;
    }

    this._selectionMode = mode;
    this._clearBrowserSelection();
    this._clearAreaSelectionPreview();
  }

  @Input() set fileUrl(url: string | undefined) {
    if (url && url !== this._fileUrl) {
      this._fileUrl = url;
      this.loadPdfFile();
    }
  }

  public loadPdfFile(): void {
    if (!this._fileUrl) {
      this.hasError = true;
      this.errorMessage = 'No file URL provided';
      this.viewerFailed.emit();
      return;
    }

    if (!this.isPdfFile()) {
      this.hasError = true;
      this.errorMessage =
        'Only PDF files are supported. For other document types, please use a different viewer.';
      this.viewerFailed.emit();
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this._needsViewerReadyEmit = false;

    const isLocalOrAbsolute = this._fileUrl.startsWith('assets/') || this._fileUrl.startsWith('http');
    const downloadUrl = isLocalOrAbsolute ? this._fileUrl : FileStorageApi.download(this._fileUrl).url;
    const headers = new HttpHeaders().set(IGNORE_ERRORS_KEY, 'true');

    this._http
      .get(downloadUrl, { headers, responseType: 'arraybuffer' })
      .pipe(
        catchError((error) => {
          this.hasError = true;
          this.errorMessage = `Failed to load PDF: ${error.status || 'Unknown error'}`;
          this.isLoading = false;
          this.viewerFailed.emit();
          return of(null);
        })
      )
      .subscribe((arrayBuffer) => {
        this.isLoading = false;
        if (arrayBuffer) {
          this.pdfSrc = new Uint8Array(arrayBuffer);
          this.viewerReady.emit();
        }
      });
  }

  /**
   * Called after pdf.js finishes rendering pages (pagesLoaded / pageRendered events).
   * Re-renders all persistent annotation overlays to handle zoom / scroll changes.
   */
  public onPagesRendered(): void {
    if (this._needsViewerReadyEmit) {
      this._needsViewerReadyEmit = false;
      this._zone.run(() => this.viewerReady.emit());
    }

    this._overlayService.onPagesRendered();
  }

  /**
   * Render a full set of our MongoDB-backed annotations as persistent overlays.
   */
  public renderAnnotationOverlays(annotations: PdfAnnotation[]): void {
    this._overlayService.setContainer(this._elementRef.nativeElement);
    this._overlayService.setAnnotations(annotations, (clicked) => {
      this.annotationClicked.emit(clicked);
    });
  }

  /**
   * Focus (scroll + pulse) a single overlay by annotation.
   */
  public focusAnnotationOverlay(annotation: PdfAnnotation): void {
    this.currentPage = annotation.pageNumber;
    this._overlayService.focusAnnotation(annotation);
  }

  /** Clear all persistent overlays. */
  public clearAnnotationOverlays(): void {
    this._overlayService.clearAll();
  }

  // Legacy Veeva highlight (kept for existing annotations that have coordinates__sys)
  public highlightAnnotation(annotation: VeevaAnnotation): boolean {
    if (!this.pdfSrc) return false;
    return this._simplePdfHighlightService.highlightAnnotation(annotation);
  }

  public clearAllHighlights(): void {
    this._simplePdfHighlightService.clearAllHighlights();
  }

  ngOnDestroy(): void {
    this._cleanupListeners.forEach((cleanup) => cleanup());
    this._clearBrowserSelection();
    this._clearAreaSelectionPreview();
    this.clearAllHighlights();
    this._overlayService.clearAll();
    this.pdfSrc = undefined;
  }

  private _registerInteractionListeners(): void {
    const host = this._elementRef.nativeElement;
    const doc = host.ownerDocument;

    this._addDomListener(host, 'pointerdown', (event: Event) => {
      this._onViewerPointerDown(event as PointerEvent);
    });
    this._addDomListener(doc, 'pointermove', (event: Event) => {
      this._onDocumentPointerMove(event as PointerEvent);
    });
    this._addDomListener(
      doc,
      'pointerup',
      (event: Event) => {
        this._onDocumentPointerUp(event as PointerEvent);
      },
      true
    );
    this._addDomListener(
      doc,
      'mouseup',
      () => {
        if (this.selectionMode !== 'text') {
          return;
        }

        queueMicrotask(() => this._emitTextSelectionDraft());
      },
      true
    );
  }

  private _addDomListener(
    target: Document | HTMLElement,
    eventName: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void {
    target.addEventListener(eventName, listener, options);
    this._cleanupListeners.push(() => target.removeEventListener(eventName, listener, options));
  }

  private _onViewerPointerDown(event: PointerEvent): void {
    if (this.selectionMode !== 'area' || event.button !== 0) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const pageContainer = target?.closest('div.page[data-page-number]') as HTMLElement | null;
    const canvas = pageContainer?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!pageContainer || !canvas) {
      return;
    }

    const canvasRect = canvas.getBoundingClientRect();
    if (!this._isPointInsideRect(event.clientX, event.clientY, canvasRect)) {
      return;
    }

    event.preventDefault();
    this._clearBrowserSelection();
    this._clearAreaSelectionPreview();

    const startPoint = this._clampPointToRect(event.clientX, event.clientY, canvasRect);
    const preview = this._createAreaPreview(pageContainer);

    this._areaSelection = {
      pageNumber: this._getPageNumber(pageContainer),
      pageContainer,
      canvas,
      preview,
      startX: startPoint.x,
      startY: startPoint.y,
      currentX: startPoint.x,
      currentY: startPoint.y,
    };

    this._updateAreaPreview();
  }

  private _onDocumentPointerMove(event: PointerEvent): void {
    if (!this._areaSelection) {
      return;
    }

    const canvasRect = this._areaSelection.canvas.getBoundingClientRect();
    const nextPoint = this._clampPointToRect(event.clientX, event.clientY, canvasRect);
    this._areaSelection.currentX = nextPoint.x;
    this._areaSelection.currentY = nextPoint.y;
    this._updateAreaPreview();
  }

  private _onDocumentPointerUp(event: PointerEvent): void {
    if (!this._areaSelection) {
      return;
    }

    const areaSelection = this._areaSelection;
    const canvasRect = areaSelection.canvas.getBoundingClientRect();
    const endPoint = this._clampPointToRect(event.clientX, event.clientY, canvasRect);
    areaSelection.currentX = endPoint.x;
    areaSelection.currentY = endPoint.y;
    this._updateAreaPreview();

    const width = Math.abs(areaSelection.currentX - areaSelection.startX);
    const height = Math.abs(areaSelection.currentY - areaSelection.startY);

    if (width < 8 || height < 8) {
      this._clearAreaSelectionPreview();
      return;
    }

    const domRect = this._buildNormalizedRect(
      areaSelection.startX,
      areaSelection.startY,
      areaSelection.currentX,
      areaSelection.currentY
    );
    const pdfRect = this._domRectToPdfRect(domRect, areaSelection.canvas, areaSelection.pageNumber);
    this._clearAreaSelectionPreview();

    if (!pdfRect) {
      return;
    }

    this._emitDraft({
      pageNumber: areaSelection.pageNumber,
      rect: pdfRect,
      rects: [pdfRect],
      textContent: 'Area selection',
      type: 'note',
      comment: 'Manual area annotation',
      color: '#FFB300',
      origin: 'manual',
      selectionMode: 'area',
    });
  }

  private _emitTextSelectionDraft(): void {
    if (this.selectionMode !== 'text') {
      return;
    }

    const selection = this._elementRef.nativeElement.ownerDocument.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    const selectedText = selection.toString().replace(/\s+/g, ' ').trim();
    if (!selectedText) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!this._selectionBelongsToViewer(range.commonAncestorContainer)) {
      return;
    }

    const pageRects = new Map<number, AnnotationRect[]>();
    for (const clientRect of Array.from(range.getClientRects())) {
      if (clientRect.width < 2 || clientRect.height < 2) {
        continue;
      }

      const pageContainer = this._findPageContainerForClientRect(clientRect);
      const canvas = pageContainer?.querySelector('canvas') as HTMLCanvasElement | null;
      if (!pageContainer || !canvas) {
        continue;
      }

      const pageNumber = this._getPageNumber(pageContainer);
      const pdfRect = this._domRectToPdfRect(clientRect, canvas, pageNumber);
      if (!pdfRect) {
        continue;
      }

      const existingRects = pageRects.get(pageNumber) ?? [];
      existingRects.push(pdfRect);
      pageRects.set(pageNumber, existingRects);
    }

    if (!pageRects.size) {
      return;
    }

    const sortedEntries = Array.from(pageRects.entries()).sort(
      ([, leftRects], [, rightRects]) => rightRects.length - leftRects.length
    );
    const [pageNumber, rects] = sortedEntries[0] ?? [];
    if (!pageNumber || !rects?.length) {
      return;
    }

    this._clearBrowserSelection();
    this._emitDraft({
      pageNumber,
      rect: this._mergeRects(rects),
      rects,
      textContent: selectedText,
      type: 'note',
      comment: 'Manual text annotation',
      color: '#FFB300',
      origin: 'manual',
      selectionMode: 'text',
    });
  }

  private _emitDraft(draft: PdfAnnotationDraft): void {
    this._zone.run(() => this.annotationDraftCreated.emit(draft));
  }

  private _selectionBelongsToViewer(node: Node): boolean {
    return this._elementRef.nativeElement.contains(
      node.nodeType === Node.TEXT_NODE ? node.parentNode : node
    );
  }

  private _findPageContainerForClientRect(clientRect: DOMRect): HTMLElement | null {
    const pageContainers = Array.from(
      this._elementRef.nativeElement.querySelectorAll('div.page[data-page-number]')
    ) as HTMLElement[];

    let bestMatch: HTMLElement | null = null;
    let bestIntersection = 0;

    for (const pageContainer of pageContainers) {
      const canvas = pageContainer.querySelector('canvas') as HTMLCanvasElement | null;
      if (!canvas) {
        continue;
      }

      const intersection = this._getRectIntersectionArea(canvas.getBoundingClientRect(), clientRect);
      if (intersection > bestIntersection) {
        bestIntersection = intersection;
        bestMatch = pageContainer;
      }
    }

    return bestMatch;
  }

  private _domRectToPdfRect(
    domRect: DomRectLike,
    canvas: HTMLCanvasElement,
    pageNumber: number
  ): AnnotationRect | null {
    const canvasRect = canvas.getBoundingClientRect();
    if (domRect.width <= 0 || domRect.height <= 0 || !this._rectsIntersect(canvasRect, domRect)) {
      return null;
    }

    const { pdfPageWidth, pdfPageHeight } = this._getPdfPageDimensions(pageNumber);
    const scaleX = canvas.offsetWidth / pdfPageWidth;
    const scaleY = canvas.offsetHeight / pdfPageHeight;
    if (!scaleX || !scaleY) {
      return null;
    }

    const left = Math.max(canvasRect.left, domRect.left);
    const top = Math.max(canvasRect.top, domRect.top);
    const right = Math.min(canvasRect.right, domRect.left + domRect.width);
    const bottom = Math.min(canvasRect.bottom, domRect.top + domRect.height);

    const width = right - left;
    const height = bottom - top;
    if (width <= 0 || height <= 0) {
      return null;
    }

    const x = (left - canvasRect.left) / scaleX;
    const topInPdf = (top - canvasRect.top) / scaleY;
    const pdfWidth = width / scaleX;
    const pdfHeight = height / scaleY;
    const y = pdfPageHeight - (topInPdf + pdfHeight);

    return {
      x,
      y,
      width: pdfWidth,
      height: pdfHeight,
      pageWidth: pdfPageWidth,
      pageHeight: pdfPageHeight,
      coordinateOrigin: 'bottom-left',
    };
  }

  private _mergeRects(rects: AnnotationRect[]): AnnotationRect {
    const minX = Math.min(...rects.map((rect) => rect.x));
    const minY = Math.min(...rects.map((rect) => rect.y));
    const maxX = Math.max(...rects.map((rect) => rect.x + rect.width));
    const maxY = Math.max(...rects.map((rect) => rect.y + rect.height));

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  private _getPdfPageDimensions(pageNumber: number): { pdfPageWidth: number; pdfPageHeight: number } {
    const defaults = { pdfPageWidth: 612, pdfPageHeight: 792 };

    try {
      const pdfApp = (window as typeof window & { PDFViewerApplication?: any }).PDFViewerApplication;
      if (pdfApp?.pdfDocument && pdfApp?.pdfViewer) {
        const pageView = pdfApp.pdfViewer.getPageView(pageNumber - 1);
        if (pageView?.pdfPage) {
          const viewport = pageView.pdfPage.getViewport({ scale: 1 });
          return { pdfPageWidth: viewport.width, pdfPageHeight: viewport.height };
        }
      }
    } catch {
      return defaults;
    }

    return defaults;
  }

  private _createAreaPreview(pageContainer: HTMLElement): HTMLDivElement {
    if (window.getComputedStyle(pageContainer).position === 'static') {
      pageContainer.style.position = 'relative';
    }

    const preview = document.createElement('div');
    preview.className = 'pdf-selection-preview';
    pageContainer.appendChild(preview);
    return preview;
  }

  private _updateAreaPreview(): void {
    if (!this._areaSelection) {
      return;
    }

    const { pageContainer, preview, startX, startY, currentX, currentY } = this._areaSelection;
    const pageRect = pageContainer.getBoundingClientRect();
    const rect = this._buildNormalizedRect(startX, startY, currentX, currentY);

    // clientLeft/clientTop equal the border width; absolute positioning
    // is measured from the padding edge (inside the border), so we must
    // subtract the border to align the preview with the cursor.
    preview.style.left = `${rect.left - pageRect.left - pageContainer.clientLeft}px`;
    preview.style.top = `${rect.top - pageRect.top - pageContainer.clientTop}px`;
    preview.style.width = `${rect.width}px`;
    preview.style.height = `${rect.height}px`;
  }

  private _buildNormalizedRect(
    startX: number,
    startY: number,
    currentX: number,
    currentY: number
  ): DomRectLike {
    return {
      left: Math.min(startX, currentX),
      top: Math.min(startY, currentY),
      width: Math.abs(currentX - startX),
      height: Math.abs(currentY - startY),
    };
  }

  private _clampPointToRect(x: number, y: number, rect: DOMRect): { x: number; y: number } {
    return {
      x: Math.min(Math.max(x, rect.left), rect.right),
      y: Math.min(Math.max(y, rect.top), rect.bottom),
    };
  }

  private _isPointInsideRect(x: number, y: number, rect: DOMRect): boolean {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  private _rectsIntersect(rect1: DomRectLike & { right?: number; bottom?: number }, rect2: DomRectLike): boolean {
    const rect1Right = 'right' in rect1 && typeof rect1.right === 'number' ? rect1.right : rect1.left + rect1.width;
    const rect1Bottom =
      'bottom' in rect1 && typeof rect1.bottom === 'number' ? rect1.bottom : rect1.top + rect1.height;
    const rect2Right = rect2.left + rect2.width;
    const rect2Bottom = rect2.top + rect2.height;

    return !(
      rect1.left >= rect2Right ||
      rect1Right <= rect2.left ||
      rect1.top >= rect2Bottom ||
      rect1Bottom <= rect2.top
    );
  }

  private _getRectIntersectionArea(rect1: DOMRect, rect2: DOMRect): number {
    const overlapWidth = Math.max(
      0,
      Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left)
    );
    const overlapHeight = Math.max(
      0,
      Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top)
    );

    return overlapWidth * overlapHeight;
  }

  private _getPageNumber(pageContainer: HTMLElement): number {
    return Number(pageContainer.dataset.pageNumber || 1);
  }

  private _clearBrowserSelection(): void {
    this._elementRef.nativeElement.ownerDocument.getSelection()?.removeAllRanges();
  }

  private _clearAreaSelectionPreview(): void {
    this._areaSelection?.preview.remove();
    this._areaSelection = null;
  }
}
